import type { ReactNode } from 'react';
import type { RailItem } from '@clarix/shared-types';
import { PanelHeader, panelTitle, type PanelTitles } from './SidebarPanel';

interface SidebarHoverPanelProps {
  panelId: string;
  /** Bounding rect of the hovered rail icon (for vertical arrow alignment). */
  anchorRect: DOMRect;
  items: readonly RailItem[];
  body: ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onCollapse: () => void;
  /** Header titles declared by the active app's sidebar config. */
  panelTitles?: PanelTitles;
}

/**
 * Floating hover panel — pops out next to the rail, anchored to the hovered
 * icon with a triangle arrow. clario-frontend parity. Distinct from the pinned
 * SidebarPanel (shadow + arrow). Body supplied by the shell.
 */
export function SidebarHoverPanel({
  panelId,
  anchorRect,
  items,
  body,
  onMouseEnter,
  onMouseLeave,
  onCollapse,
  panelTitles,
}: SidebarHoverPanelProps) {
  const left = 64; // rail width
  // Full height, like the pinned panel: the title sits in the TopBar's row.
  const top = 0;
  const cardHeight = (typeof window !== 'undefined' ? window.innerHeight : 800) - top;
  const arrowTop = anchorRect.top + anchorRect.height / 2 - top;

  return (
    <div
      className="fixed z-50 hidden lg:block"
      style={{ left, top }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Triangle indicator aligned to the hovered icon. */}
      <div className="absolute -left-[7px]" style={{ top: arrowTop - 7 }}>
        <div className="h-0 w-0 border-b-[7px] border-r-[7px] border-t-[7px] border-b-transparent border-r-gray-200 border-t-transparent" />
        <div className="absolute left-[1px] top-[1px] h-0 w-0 border-b-[6px] border-r-[6px] border-t-[6px] border-b-transparent border-r-white border-t-transparent" />
      </div>

      <div
        className="shadow-overlay relative flex w-[var(--sidebar-panel-w,14rem)] flex-col overflow-hidden bg-white"
        style={{ height: cardHeight }}
      >
        <PanelHeader title={panelTitle(panelId, items, panelTitles)} onCollapse={onCollapse} />
        <div className="scrollbar-hide flex-1 overflow-y-auto">{body}</div>
      </div>
    </div>
  );
}
